import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, Check, X, 
  Play, RefreshCw, ArrowLeft
} from 'lucide-react';
import { API } from '../../lib/api';
import { Button } from '../../components/ui/button';
import AdminLayout from './AdminLayout';

export default function CleaningCalendar() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthData, setMonthData] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // New cleaning form
  const [newCleaning, setNewCleaning] = useState({
    unit_id: '',
    unit_name: '',
    scheduled_date: '',
    reason: 'Pulizia manuale',
    notes: ''
  });

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Fetch month data
  const fetchMonthData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    
    const monthTasks = [];
    
    // Fetch data for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      try {
        const res = await axios.get(`${API}/admin/cleanings/week`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start_date: dateStr }
        });
        
        // Get data for just this day
        const dayData = res.data.days?.find(d => d.date === dateStr);
        if (dayData) {
          monthTasks.push(dayData);
        }
      } catch (err) {
        console.error(`Error fetching day ${dateStr}:`, err);
      }
    }
    
    setMonthData(monthTasks);
    setLoading(false);
  }, [token, currentDate]);

  // Fetch units
  const fetchUnits = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/units`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnits(res.data || []);
    } catch (err) {
      console.error('Error fetching units:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  useEffect(() => {
    fetchMonthData();
  }, [fetchMonthData]);

  // Navigate months
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDay(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  // Update cleaning status
  const updateCleaningStatus = async (taskId, newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/admin/cleanings/${taskId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        newStatus === 'ready' ? 'Camera pronta per il check-in!' :
        newStatus === 'completed' ? 'Camera segnata come pulita!' : 
        newStatus === 'in_progress' ? 'Pulizia iniziata' : 'Stato aggiornato'
      );
      await fetchMonthData();
      setShowTaskModal(false);
      setSelectedTask(null);
    } catch (err) {
      console.error('Error updating cleaning:', err);
      toast.error('Errore aggiornamento stato');
    } finally {
      setUpdating(false);
    }
  };

  // Create manual cleaning
  const createManualCleaning = async () => {
    if (!newCleaning.unit_id || !newCleaning.scheduled_date) {
      toast.error('Seleziona struttura e data');
      return;
    }
    
    setUpdating(true);
    try {
      await axios.post(`${API}/admin/cleanings/manual`, newCleaning, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pulizia aggiunta');
      await fetchMonthData();
      setShowAddModal(false);
      setNewCleaning({
        unit_id: '',
        unit_name: '',
        scheduled_date: '',
        reason: 'Pulizia manuale',
        notes: ''
      });
    } catch (err) {
      console.error('Error creating cleaning:', err);
      toast.error('Errore creazione pulizia');
    } finally {
      setUpdating(false);
    }
  };

  // Render calendar grid
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    // Adjust for Monday start (0 = Monday in our grid)
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;
    
    const days = [];
    const today = new Date().toISOString().split('T')[0];
    
    // Empty cells before first day
    for (let i = 0; i < startOffset; i++) {
      days.push(<div key={`empty-${i}`} className="h-20 bg-gray-50" />);
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = monthData.find(d => d.date === dateStr);
      const isToday = dateStr === today;
      const isSelected = selectedDay?.date === dateStr;
      
      days.push(
        <div
          key={dateStr}
          onClick={() => setSelectedDay(dayData || { date: dateStr, tasks: [], total: 0 })}
          className={`h-20 p-1 border cursor-pointer transition-all ${
            isSelected ? 'ring-2 ring-purple-500 bg-purple-50' :
            isToday ? 'bg-blue-50 border-blue-200' : 'bg-white hover:bg-gray-50'
          }`}
        >
          <div className="flex items-start justify-between">
            <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
              {day}
            </span>
            {dayData && dayData.total > 0 && (
              <span className={`text-xs font-bold px-1.5 rounded ${
                dayData.status === 'pending' ? 'bg-red-100 text-red-600' :
                dayData.status === 'in_progress' ? 'bg-orange-100 text-orange-600' :
                'bg-green-100 text-green-600'
              }`}>
                {dayData.total}
              </span>
            )}
          </div>
          {dayData && dayData.total > 0 && (
            <div className="mt-1 space-y-0.5">
              {dayData.tasks.slice(0, 2).map((task, i) => (
                <div key={i} className={`text-[9px] px-1 py-0.5 rounded truncate ${
                  task.status === 'ready' ? 'bg-green-200 text-green-800' :
                  task.status === 'completed' ? 'bg-green-100 text-green-700' :
                  task.status === 'in_progress' ? 'bg-orange-100 text-orange-700' :
                  task.priority === 'critical' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {task.unit_name}
                </div>
              ))}
              {dayData.tasks.length > 2 && (
                <div className="text-[9px] text-gray-400">+{dayData.tasks.length - 2} altre</div>
              )}
            </div>
          )}
        </div>
      );
    }
    
    return days;
  };

  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

  return (
    <AdminLayout>
      <div className="p-4 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Calendario Pulizie</h1>
              <p className="text-sm text-gray-500">Pianifica e gestisci le pulizie mensili</p>
            </div>
          </div>
          <Button
            onClick={() => {
              setNewCleaning({...newCleaning, scheduled_date: new Date().toISOString().split('T')[0]});
              setShowAddModal(true);
            }}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Aggiungi Pulizia
          </Button>
        </div>

        {/* Calendar Navigation */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-4">
            <button onClick={goToPreviousMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-800">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <button onClick={goToToday} className="text-sm text-purple-600 hover:underline">
                Vai a oggi
              </button>
            </div>
            <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mb-4 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-100 rounded"></span> Da fare</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-100 rounded"></span> In corso</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-100 rounded"></span> Completate</span>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {renderCalendar()}
            </div>
          )}
        </div>

        {/* Selected day details */}
        {selectedDay && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-purple-50 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-gray-800">
                  {new Date(selectedDay.date).toLocaleDateString('it-IT', { 
                    weekday: 'long', day: 'numeric', month: 'long' 
                  })}
                </span>
              </div>
              <div className="flex gap-2 text-sm">
                {selectedDay.pending > 0 && <span className="text-red-600">🔴 {selectedDay.pending}</span>}
                {selectedDay.in_progress > 0 && <span className="text-orange-500">🟠 {selectedDay.in_progress}</span>}
                {selectedDay.completed > 0 && <span className="text-green-600">🟢 {selectedDay.completed}</span>}
              </div>
            </div>
            
            <div className="divide-y">
              {selectedDay.tasks?.length === 0 ? (
                <div className="p-8 text-center">
                  <Check className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-600">Nessuna pulizia prevista</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setNewCleaning({...newCleaning, scheduled_date: selectedDay.date});
                      setShowAddModal(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi pulizia
                  </Button>
                </div>
              ) : (
                selectedDay.tasks.map(task => (
                  <div
                    key={task.id}
                    onClick={() => { setSelectedTask(task); setShowTaskModal(true); }}
                    className={`p-4 cursor-pointer transition-colors ${
                      task.status === 'ready' ? 'bg-green-50 hover:bg-green-100' :
                      task.status === 'completed' ? 'bg-green-50/50 hover:bg-green-100' :
                      task.status === 'in_progress' ? 'bg-orange-50 hover:bg-orange-100' :
                      task.priority === 'critical' ? 'bg-red-50 hover:bg-red-100' :
                      'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {task.status === 'ready' ? '✅' :
                           task.status === 'completed' ? '🟢' :
                           task.status === 'in_progress' ? '🟠' : '🔴'}
                        </span>
                        <div>
                          <p className="font-semibold text-gray-800">{task.unit_name}</p>
                          {task.guest_name && task.guest_name !== 'Ospite' && (
                            <p className="text-sm text-gray-600">{task.guest_name}</p>
                          )}
                          <p className="text-xs text-gray-500">{task.reason}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {task.has_new_arrival && (
                          <div className="text-xs text-red-600 font-medium mb-1">
                            ⚠️ Arrivo {task.next_checkin_time || '15:00'}
                          </div>
                        )}
                        {task.checkout_time && (
                          <div className="text-xs text-gray-500">
                            Check-out: {task.checkout_time}
                          </div>
                        )}
                        <span className={`text-sm font-medium ${
                          task.status === 'ready' ? 'text-green-700' :
                          task.status === 'completed' ? 'text-green-600' :
                          task.status === 'in_progress' ? 'text-orange-600' : 
                          task.priority === 'critical' ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {task.status === 'ready' ? 'PRONTA' :
                           task.status === 'completed' ? 'Pulita' :
                           task.status === 'in_progress' ? 'In corso' : 
                           task.priority === 'critical' ? 'URGENTE' : 'Da fare'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Task Modal */}
        {showTaskModal && selectedTask && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-lg">{selectedTask.unit_name}</h2>
                  {selectedTask.guest_name && selectedTask.guest_name !== 'Ospite' && (
                    <p className="text-purple-100 text-sm">{selectedTask.guest_name}</p>
                  )}
                </div>
                <button onClick={() => setShowTaskModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-3">                
                {selectedTask.checkout_time && (
                  <div className="bg-orange-50 rounded-lg p-3">
                    <p className="text-xs text-orange-600">Check-out</p>
                    <p className="font-medium text-orange-700">Entro le {selectedTask.checkout_time}</p>
                  </div>
                )}
                
                {selectedTask.has_new_arrival && (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <p className="text-xs text-red-600">⚠️ Prossimo check-in</p>
                    <p className="font-medium text-red-700">{selectedTask.next_guest || 'Nuovo ospite'}</p>
                    <p className="text-xs text-red-500">Arrivo alle {selectedTask.next_checkin_time || '15:00'}</p>
                  </div>
                )}
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Motivo</p>
                  <p className="font-medium">{selectedTask.reason}</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Stato</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xl">
                      {selectedTask.status === 'ready' ? '✅' :
                       selectedTask.status === 'completed' ? '🟢' :
                       selectedTask.status === 'in_progress' ? '🟠' : '🔴'}
                    </span>
                    <span className="font-medium">
                      {selectedTask.status === 'ready' ? 'Pronta per check-in' :
                       selectedTask.status === 'completed' ? 'Pulita' :
                       selectedTask.status === 'in_progress' ? 'In corso' : 'Da fare'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border-t space-y-2">
                {selectedTask.status === 'pending' && (
                  <>
                    <Button
                      onClick={() => updateCleaningStatus(selectedTask.id, 'in_progress')}
                      disabled={updating}
                      className="w-full bg-orange-500 hover:bg-orange-600"
                    >
                      {updating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                      Inizia Pulizia
                    </Button>
                    <Button
                      onClick={() => updateCleaningStatus(selectedTask.id, 'ready')}
                      disabled={updating}
                      className="w-full bg-green-500 hover:bg-green-600"
                    >
                      {updating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      ✓ Camera Pronta
                    </Button>
                  </>
                )}
                
                {selectedTask.status === 'in_progress' && (
                  <Button
                    onClick={() => updateCleaningStatus(selectedTask.id, 'ready')}
                    disabled={updating}
                    className="w-full bg-green-500 hover:bg-green-600 py-3"
                  >
                    {updating ? <RefreshCw className="w-5 h-5 animate-spin mr-2" /> : <Check className="w-5 h-5 mr-2" />}
                    ✓ CAMERA PRONTA
                  </Button>
                )}
                
                {(selectedTask.status === 'completed' || selectedTask.status === 'ready') && (
                  <div className="text-center py-2 text-green-600 font-medium">
                    {selectedTask.status === 'ready' ? '✅ Camera pronta per il check-in' : '✓ Pulizia completata'}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Cleaning Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-md rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-lg">Nuova Pulizia</h2>
                  <p className="text-purple-100 text-sm">Aggiungi manualmente</p>
                </div>
                <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-white/20 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Struttura *</label>
                  <select
                    value={newCleaning.unit_id}
                    onChange={(e) => {
                      const unit = units.find(u => u.id === e.target.value);
                      setNewCleaning({
                        ...newCleaning,
                        unit_id: e.target.value,
                        unit_name: unit?.nome || ''
                      });
                    }}
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="">Seleziona struttura</option>
                    {units.map(unit => (
                      <option key={unit.id} value={unit.id}>{unit.nome}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                  <input
                    type="date"
                    value={newCleaning.scheduled_date}
                    onChange={(e) => setNewCleaning({...newCleaning, scheduled_date: e.target.value})}
                    className="w-full border rounded-lg p-2"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                  <select
                    value={newCleaning.reason}
                    onChange={(e) => setNewCleaning({...newCleaning, reason: e.target.value})}
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="Pulizia manuale">Pulizia manuale</option>
                    <option value="Pulizia straordinaria">Pulizia straordinaria</option>
                    <option value="Sanificazione">Sanificazione</option>
                    <option value="Manutenzione">Manutenzione</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                  <textarea
                    value={newCleaning.notes}
                    onChange={(e) => setNewCleaning({...newCleaning, notes: e.target.value})}
                    className="w-full border rounded-lg p-2 h-20"
                    placeholder="Note aggiuntive..."
                  />
                </div>
              </div>
              
              <div className="p-4 border-t flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={createManualCleaning}
                  disabled={updating}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {updating ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Aggiungi
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
